/**
 * Regex pattern for ISO 8601 date format (YYYY-MM-DD)
 */
export const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

export const daysUntilFromUnix = (unixTimestamp: number): number => {
  const oneDay = 24 * 60 * 60 * 1000 // milliseconds in a day
  const futureDate = new Date(unixTimestamp * 1000)
  const currentDate = new Date()

  const diffInTime = futureDate.getTime() - currentDate.getTime()
  const diffInDays = Math.ceil(diffInTime / oneDay)

  return diffInDays
}

// Constants for determining English ordinal suffixes using Intl.PluralRules
const ORDINAL_PLURAL_RULES = new Intl.PluralRules('en-US', { type: 'ordinal' })
const ORDINAL_SUFFIXES: Record<string, string> = {
  one: 'st',
  two: 'nd',
  few: 'rd',
  other: 'th',
}

/**
 * Formats a timestamp (milliseconds) into a date string with month and day,
 * using an English ordinal suffix.
 *
 * @param timestampMs - JS timestamp in milliseconds (e.g. 1721356800000 for July 19, 2024)
 * @returns Formatted date string (e.g. "July 19th")
 */
export const timestampMsToMonthDayOrdinal = (timestampMs: number): string => {
  const date = new Date(timestampMs)
  const month = date.toLocaleString('en-US', { month: 'long' })
  const day = date.getDate()
  const rule = ORDINAL_PLURAL_RULES.select(day) // e.g. "one", "other"
  const suffix = ORDINAL_SUFFIXES[rule] ?? 'th'
  return `${month} ${day}${suffix}`
}

/**
 * Formats a timestamp (milliseconds) into separate date and time strings.
 *
 * @param timestampMs - JS timestamp in milliseconds (e.g. 1721356800000 for July 19, 2024)
 * @param locale - Locale to use for formatting (default: "en-US")
 * @returns Object with date and time parts as strings (e.g. { date: "Jul 19, 2024", time: "12:00 PM" })
 */
export const timestampMsToDateTimeParts = (timestampMs: number, locale = 'en-US') => {
  const d = new Date(timestampMs)
  const dtf = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

  const parts = dtf.formatToParts(d)
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''

  const date = `${get('month')} ${get('day')}, ${get('year')}`
  const time = `${get('hour')}:${get('minute')} ${get('dayPeriod')}`.trim()

  return { date, time }
}

/**
 * Formats a timestamp (milliseconds) into a date and time string.
 *
 * @param timestampMs - JS timestamp in milliseconds (e.g. 1721356800000 for July 19, 2024)
 * @param opts - Optional options object
 * @param opts.separator - Separator between date and time (default: " ")
 * @param opts.locale - Locale to use for formatting (default: "en-US")
 * @returns Formatted date and time string (e.g. "Jul 19, 2024 12:00 PM")
 */
export const timestampMsToDateTimeString = (timestampMs: number, opts: { separator?: string; locale?: string } = {}) => {
  const { separator = ' ', locale = 'en-US' } = opts
  const { date, time } = timestampMsToDateTimeParts(timestampMs, locale)
  return `${date}${separator}${time}`
}

/**
 * Formats a timestamp (milliseconds) into a short month, day, and time string.
 * Format: "Dec 12, 12:52PM"
 *
 * @param timestampMs - JS timestamp in milliseconds (e.g. 1702392720000)
 * @returns Formatted month, day, and time string (e.g. "Dec 12, 12:52PM")
 */
export const timestampMsToMonthDayTime = (timestampMs: number): string => {
  const date = new Date(timestampMs)
  const month = date.toLocaleString('en-US', { month: 'short' })
  const day = date.getDate()
  const hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12

  return `${month} ${day}, ${displayHours}:${minutes}${period}`
}

/**
 * Formats an ISO 8601 date string (YYYY-MM-DD) to US display format (MM/DD/YYYY).
 *
 * This function expects input that has already been validated by Zod (via parseISODate) at the API boundary.
 * It includes a lightweight format check as a safety net and contract enforcement.
 *
 * @param isoDateString - Date string in ISO 8601 format (e.g. "2024-07-19")
 * @returns Formatted date string in MM/DD/YYYY format (e.g. "07/19/2024")
 * @throws Error if the input is not in valid ISO 8601 format (YYYY-MM-DD)
 */
export const isoDateStringToUSFormat = (isoDateString: string): string => {
  // Lightweight format check - enforces function contract and catches misuse
  if (!ISO_DATE_REGEX.test(isoDateString)) {
    throw new Error(`Invalid ISO date format: "${isoDateString}". Expected YYYY-MM-DD format.`)
  }

  const [year, month, day] = isoDateString.split('-')
  return `${month}/${day}/${year}`
}

/**
 * Formats an ISO 8601 date string (YYYY-MM-DD) to a month-name display format (e.g. "Jan 15, 2023").
 *
 * Like {@link isoDateStringToUSFormat}, this parses the Y/M/D directly off the string rather than
 * constructing a `Date` from it, so it is immune to the UTC-vs-local timezone off-by-one that bites
 * date-only values. The numeric parts are fed into a fixed UTC `Date` purely to resolve the month name.
 *
 * This function expects input that has already been validated by Zod (via parseISODate) at the API boundary.
 * It includes a lightweight format check as a safety net and contract enforcement.
 *
 * @param isoDateString - Date string in ISO 8601 format (e.g. "2023-01-15")
 * @returns Formatted date string (e.g. "Jan 15, 2023")
 * @throws Error if the input is not in valid ISO 8601 format (YYYY-MM-DD)
 */
export const isoDateStringToMonthDayYear = (isoDateString: string): string => {
  // Lightweight format check - enforces function contract and catches misuse
  if (!ISO_DATE_REGEX.test(isoDateString)) {
    throw new Error(`Invalid ISO date format: "${isoDateString}". Expected YYYY-MM-DD format.`)
  }

  const [year, month, day] = isoDateString.split('-').map(Number)
  // Build the date in UTC and format in UTC so the day/month never shift across the local TZ boundary.
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

/**
 * Formats a past timestamp (ms) as a short relative string, e.g. "just now", "5 min ago",
 * "3 hr ago", "2 days ago". Falls back to an absolute "Mon D, YYYY" date for anything a week
 * or older. `nowMs` is injectable for deterministic tests.
 */
// Relative time-ago formatting is consolidated in @/matter/utils/format-last-activity
// (formatLastActivityForCell) so the chronology + Matter Overview render it identically.
